import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callAvosApi } from "../../../utils/useAvosApiHook";

export const RULES_KEY = "signal-rules";

export interface GlobalRule {
    key: string;
    category: string;
    severity: string | null;
    evaluator_type: string;
    params: Record<string, any>;
    message_template: string;
    recommended_action_template: string | null;
    enabled: boolean;
}

export interface RuleTenantState {
    key: string;
    tenant_id: string;
    enabled: boolean;
    params_override: Record<string, any> | null;
    global_enabled: boolean;
    global_params: Record<string, any>;
}

export interface ParamsOverrideResult {
    override: Record<string, any> | null;
    errors: Record<string, string>;
}

/** Turn the edit-modal draft (string inputs per param) into a params_override,
 *  validating numeric fields and dropping values equal to the global default.
 *  An empty input clears that param's override (falls back to the global). */
export const buildParamsOverride = (
    draft: Record<string, string>,
    globalParams: Record<string, any>,
): ParamsOverrideResult => {
    const override: Record<string, any> = {};
    const errors: Record<string, string> = {};

    Object.keys(globalParams).forEach((key) => {
        const raw = (draft[key] ?? "").trim();
        if (raw === "") return; // cleared -> use the global default (no override)

        const globalValue = globalParams[key];
        if (typeof globalValue === "number") {
            const num = Number(raw);
            if (Number.isNaN(num)) {
                errors[key] = "Must be a number";
                return;
            }
            if (num !== globalValue) override[key] = num;
        } else if (globalValue === null || globalValue === undefined) {
            // Blocked-on-TBD param: coerce numeric strings to numbers.
            const num = Number(raw);
            override[key] = Number.isNaN(num) ? raw : num;
        } else if (raw !== String(globalValue)) {
            override[key] = raw;
        }
    });

    return { override: Object.keys(override).length ? override : null, errors };
};

/** The global signal-rule definitions (EFQ-consultant only). */
export const useSignalRules = () => useQuery({
    queryKey: [RULES_KEY, "global"],
    queryFn: async (): Promise<GlobalRule[]> => {
        const { data } = await callAvosApi("/signal/rules");
        return data;
    },
});

/** The per-tenant availability + override for one rule. */
export const useRuleTenant = (ruleKey: string, tenant: string, enabled = true) => useQuery({
    queryKey: [RULES_KEY, tenant, ruleKey],
    queryFn: async (): Promise<RuleTenantState> => {
        const { data } = await callAvosApi(`/signal/rules/${ruleKey}/tenant/${tenant}`);
        return data;
    },
    enabled: enabled && !!ruleKey && !!tenant,
});

/** Upsert a rule's per-tenant availability + params_override; refetches the
 *  tenant's rule state. */
export const useUpdateRuleTenant = (tenant: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ ruleKey, enabled, params_override }: {
            ruleKey: string;
            enabled: boolean;
            params_override: Record<string, any> | null;
        }): Promise<RuleTenantState> => {
            const { data } = await callAvosApi(`/signal/rules/${ruleKey}/tenant/${tenant}`, {
                method: "PUT",
                data: { enabled, params_override },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [RULES_KEY, tenant], exact: false });
        },
    });
};
